import React, { useState, useRef } from 'react';
import './Gallery.css';

const Gallery = () => {
    const [photos, setPhotos] = useState([
        {
            id: 1,
            title: "Sunset View",
            description: "Beautiful sunset at the beach",
            category: "Nature",
            tags: ["sunset", "beach", "evening"],
            url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2024-01-15",
            likes: 42
        },
        {
            id: 2,
            title: "City Lights",
            description: "Night view of city skyline",
            category: "Urban",
            tags: ["city", "night", "lights"],
            url: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2024-01-10",
            likes: 38
        },
        {
            id: 3,
            title: "Mountain Peak",
            description: "Snow covered mountain peak",
            category: "Nature",
            tags: ["mountain", "snow", "landscape"],
            url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2024-01-05",
            likes: 56
        },
        {
            id: 4,
            title: "Modern Architecture",
            description: "Contemporary building design",
            category: "Architecture",
            tags: ["building", "modern", "design"],
            url: "https://images.unsplash.com/photo-1487956382158-bb926046304a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2024-01-02",
            likes: 29
        },
        {
            id: 5,
            title: "Street Art",
            description: "Colorful graffiti on city walls",
            category: "Art",
            tags: ["graffiti", "colorful", "urban"],
            url: "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2023-12-28",
            likes: 47
        },
        {
            id: 6,
            title: "Wildlife Portrait",
            description: "Close up of a wild animal",
            category: "Wildlife",
            tags: ["animal", "wild", "portrait"],
            url: "https://images.unsplash.com/photo-1564349683136-77e08dba1ef7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
            date: "2023-12-20",
            likes: 63
        }
    ]);

    const [newPhoto, setNewPhoto] = useState({
        title: '',
        description: '',
        category: 'Nature',
        tags: '',
        file: null,
        preview: ''
    });

    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [editingPhoto, setEditingPhoto] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    const fileInputRef = useRef(null);

    const categories = ['All', 'Nature', 'Urban', 'Architecture', 'Art', 'Wildlife', 'Portrait', 'Travel', 'Food'];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setNewPhoto({
                ...newPhoto,
                file: file,
                preview: previewUrl
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPhoto({
            ...newPhoto,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!newPhoto.file && !newPhoto.preview) {
            alert('Please select an image file');
            return;
        }

        const newPhotoObj = {
            id: photos.length + 1,
            title: newPhoto.title || 'Untitled',
            description: newPhoto.description,
            category: newPhoto.category,
            tags: newPhoto.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
            url: newPhoto.preview || 'https://via.placeholder.com/400x300',
            date: new Date().toISOString().split('T')[0],
            likes: 0
        };

        setPhotos([newPhotoObj, ...photos]);

        // Reset form
        setNewPhoto({
            title: '',
            description: '',
            category: 'Nature',
            tags: '',
            file: null,
            preview: ''
        });

        setShowUploadModal(false);

        // Clean up object URL
        if (newPhoto.preview) {
            URL.revokeObjectURL(newPhoto.preview);
        }
    };

    const handleEdit = (photo) => {
        setEditingPhoto({ ...photo });
        setShowUploadModal(true);
    };

    const handleUpdate = (e) => {
        e.preventDefault();

        setPhotos(photos.map(photo =>
            photo.id === editingPhoto.id ? editingPhoto : photo
        ));

        setEditingPhoto(null);
        setShowUploadModal(false);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this photo?')) {
            setPhotos(photos.filter(photo => photo.id !== id));
        }
    };

    const handleLike = (id) => {
        setPhotos(photos.map(photo =>
            photo.id === id ? { ...photo, likes: photo.likes + 1 } : photo
        ));
    };

    // Filter and sort photos
    const filteredPhotos = photos
        .filter(photo =>
            (selectedCategory === 'All' || photo.category === selectedCategory) &&
            (searchTerm === '' ||
                photo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                photo.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                photo.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
        )
        .sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.date) - new Date(a.date);
                case 'oldest':
                    return new Date(a.date) - new Date(b.date);
                case 'likes':
                    return b.likes - a.likes;
                case 'title':
                    return a.title.localeCompare(b.title);
                default:
                    return 0;
            }
        });

    const openLightbox = (photo) => {
        setSelectedPhoto(photo);
    };

    const closeLightbox = () => {
        setSelectedPhoto(null);
    };

    return (
        <div className="gallery-new-container">
            {/* Header */}
            <div className="gallery-new-header">
                <h1 className="gallery-new-title">Photo Gallery</h1>
                <p className="gallery-new-subtitle">Upload, organize and browse your photos</p>

                <div className="gallery-new-controls">
                    <div className="gallery-new-search-box">
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search photos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="gallery-new-filter-controls">
                        <div className="gallery-new-category-filter">
                            <i className="fas fa-filter"></i>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>

                        <div className="gallery-new-sort-filter">
                            <i className="fas fa-sort"></i>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="likes">Most Liked</option>
                                <option value="title">Title A-Z</option>
                            </select>
                        </div>

                        <button
                            className="gallery-new-upload-btn"
                            onClick={() => {
                                setEditingPhoto(null);
                                setShowUploadModal(true);
                            }}
                        >
                            <i className="fas fa-cloud-upload-alt"></i>
                            Upload Photo
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="gallery-new-stats">
                <div className="gallery-new-stat-card">
                    <i className="fas fa-images"></i>
                    <div>
                        <h3>{photos.length}</h3>
                        <p>Total Photos</p>
                    </div>
                </div>
                <div className="gallery-new-stat-card">
                    <i className="fas fa-heart"></i>
                    <div>
                        <h3>{photos.reduce((sum, photo) => sum + photo.likes, 0)}</h3>
                        <p>Total Likes</p>
                    </div>
                </div>
                <div className="gallery-new-stat-card">
                    <i className="fas fa-tags"></i>
                    <div>
                        <h3>{categories.length - 1}</h3>
                        <p>Categories</p>
                    </div>
                </div>
            </div>

            {/* Category Tabs */}
            <div className="gallery-new-category-tabs">
                {categories.filter(cat => cat !== 'All').map(category => (
                    <button
                        key={category}
                        className={`gallery-new-category-tab ${selectedCategory === category ? 'gallery-new-active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category}
                        <span className="gallery-new-tab-count">
                            {photos.filter(photo => photo.category === category).length}
                        </span>
                    </button>
                ))}
            </div>

            {/* Photo Grid */}
            <div className="gallery-new-photo-grid">
                {filteredPhotos.length === 0 ? (
                    <div className="gallery-new-no-photos">
                        <i className="fas fa-camera"></i>
                        <h3>No photos found</h3>
                        <p>Try a different search or upload new photos</p>
                    </div>
                ) : (
                    filteredPhotos.map(photo => (
                        <div key={photo.id} className="gallery-new-photo-card">
                            <div className="gallery-new-photo-wrapper" onClick={() => openLightbox(photo)}>
                                <img src={photo.url} alt={photo.title} />
                                <div className="gallery-new-photo-overlay">
                                    <button className="gallery-new-view-btn">
                                        <i className="fas fa-expand"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="gallery-new-photo-info">
                                <div className="gallery-new-photo-header">
                                    <h3>{photo.title}</h3>
                                    <div className="gallery-new-photo-actions">
                                        <button
                                            className="gallery-new-like-btn"
                                            onClick={() => handleLike(photo.id)}
                                        >
                                            <i className="fas fa-heart"></i>
                                            {photo.likes}
                                        </button>
                                        <div className="gallery-new-dropdown">
                                            <button className="gallery-new-more-btn">
                                                <i className="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div className="gallery-new-dropdown-content">
                                                <button onClick={() => handleEdit(photo)}>
                                                    <i className="fas fa-edit"></i> Edit
                                                </button>
                                                <button onClick={() => handleDelete(photo.id)}>
                                                    <i className="fas fa-trash"></i> Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <p className="gallery-new-photo-desc">{photo.description}</p>

                                <div className="gallery-new-photo-meta">
                                    <span className="gallery-new-category-badge">{photo.category}</span>
                                    <span className="gallery-new-photo-date">
                                        <i className="far fa-calendar"></i> {photo.date}
                                    </span>
                                </div>

                                <div className="gallery-new-photo-tags">
                                    {photo.tags.map((tag, index) => (
                                        <span key={index} className="gallery-new-tag">{tag}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Upload/Edit Modal */}
            {showUploadModal && (
                <div className="gallery-new-modal-overlay">
                    <div className="gallery-new-modal-content">
                        <div className="gallery-new-modal-header">
                            <h2>{editingPhoto ? 'Edit Photo' : 'Upload New Photo'}</h2>
                            <button
                                className="gallery-new-close-btn"
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setEditingPhoto(null);
                                    if (newPhoto.preview) {
                                        URL.revokeObjectURL(newPhoto.preview);
                                    }
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={editingPhoto ? handleUpdate : handleSubmit}>
                            <div className="gallery-new-form-row">
                                <div className="gallery-new-form-group">
                                    <label>Photo Title *</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={editingPhoto ? editingPhoto.title : newPhoto.title}
                                        onChange={editingPhoto ?
                                            (e) => setEditingPhoto({ ...editingPhoto, title: e.target.value }) :
                                            handleInputChange
                                        }
                                        required
                                        placeholder="Enter photo title"
                                    />
                                </div>

                                <div className="gallery-new-form-group">
                                    <label>Category</label>
                                    <select
                                        name="category"
                                        value={editingPhoto ? editingPhoto.category : newPhoto.category}
                                        onChange={editingPhoto ?
                                            (e) => setEditingPhoto({ ...editingPhoto, category: e.target.value }) :
                                            handleInputChange
                                        }
                                    >
                                        {categories.filter(cat => cat !== 'All').map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="gallery-new-form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={editingPhoto ? editingPhoto.description : newPhoto.description}
                                    onChange={editingPhoto ?
                                        (e) => setEditingPhoto({ ...editingPhoto, description: e.target.value }) :
                                        handleInputChange
                                    }
                                    rows="3"
                                    placeholder="Describe your photo..."
                                />
                            </div>

                            <div className="gallery-new-form-group">
                                <label>Tags (comma separated)</label>
                                <input
                                    type="text"
                                    name="tags"
                                    value={editingPhoto ? editingPhoto.tags.join(', ') : newPhoto.tags}
                                    onChange={editingPhoto ?
                                        (e) => setEditingPhoto({ ...editingPhoto, tags: e.target.value.split(',').map(tag => tag.trim()) }) :
                                        handleInputChange
                                    }
                                    placeholder="e.g., sunset, beach, nature"
                                />
                            </div>

                            {!editingPhoto && (
                                <div className="gallery-new-form-group">
                                    <label>Select Image *</label>
                                    <div className="gallery-new-file-upload">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            className="gallery-new-file-btn"
                                            onClick={() => fileInputRef.current.click()}
                                        >
                                            <i className="fas fa-cloud-upload-alt"></i>
                                            Choose File
                                        </button>
                                        <span className="gallery-new-file-name">
                                            {newPhoto.file ? newPhoto.file.name : 'No file chosen'}
                                        </span>
                                    </div>

                                    {newPhoto.preview && (
                                        <div className="gallery-new-image-preview">
                                            <img src={newPhoto.preview} alt="Preview" />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="gallery-new-modal-actions">
                                <button
                                    type="button"
                                    className="gallery-new-cancel-btn"
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setEditingPhoto(null);
                                        if (newPhoto.preview) {
                                            URL.revokeObjectURL(newPhoto.preview);
                                        }
                                    }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="gallery-new-submit-btn">
                                    {editingPhoto ? 'Update Photo' : 'Upload Photo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Lightbox */}
            {selectedPhoto && (
                <div className="gallery-new-lightbox-overlay" onClick={closeLightbox}>
                    <div className="gallery-new-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="gallery-new-lightbox-close" onClick={closeLightbox}>
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="gallery-new-lightbox-body">
                            <div className="gallery-new-lightbox-image">
                                <img src={selectedPhoto.url} alt={selectedPhoto.title} />
                            </div>

                            <div className="gallery-new-lightbox-info">
                                <h2>{selectedPhoto.title}</h2>
                                <p>{selectedPhoto.description}</p>

                                <div className="gallery-new-lightbox-meta">
                                    <div className="gallery-new-meta-item">
                                        <i className="fas fa-tag"></i>
                                        <span>{selectedPhoto.category}</span>
                                    </div>
                                    <div className="gallery-new-meta-item">
                                        <i className="far fa-calendar"></i>
                                        <span>{selectedPhoto.date}</span>
                                    </div>
                                    <div className="gallery-new-meta-item">
                                        <i className="fas fa-heart"></i>
                                        <span>{selectedPhoto.likes} likes</span>
                                    </div>
                                </div>

                                <div className="gallery-new-lightbox-tags">
                                    {selectedPhoto.tags.map((tag, index) => (
                                        <span key={index} className="gallery-new-tag">{tag}</span>
                                    ))}
                                </div>

                                <div className="gallery-new-lightbox-actions">
                                    <button
                                        className="gallery-new-action-btn"
                                        onClick={() => handleLike(selectedPhoto.id)}
                                    >
                                        <i className="fas fa-heart"></i> Like
                                    </button>
                                    <button className="gallery-new-action-btn">
                                        <i className="fas fa-download"></i> Download
                                    </button>
                                    <button className="gallery-new-action-btn">
                                        <i className="fas fa-share"></i> Share
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;